import type { CreateResumeDto, ResumeDto } from "@reactive-resume/dto";
import type { ResumeData } from "@reactive-resume/schema";
import type { Template } from "@reactive-resume/utils";
import { useMutation } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";

import { applyTemplateThemeToData } from "@/client/constants/template-presets";
import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";

export const createResume = async (data: CreateResumeDto) => {
  const response = await axios.post<ResumeDto, AxiosResponse<ResumeDto>, CreateResumeDto>(
    "/resume",
    data,
  );

  const created = response.data;

  const template = created.data.metadata.template as Template;

  const themedData = applyTemplateThemeToData(created.data, template);

  /*
   * Si la couleur retournée par
   * la création est déjà correcte,
   * aucune requête supplémentaire
   * n’est envoyée.
   */
  if (
    themedData.metadata.theme.primary === created.data.metadata.theme.primary &&
    themedData.metadata.theme.background === created.data.metadata.theme.background &&
    themedData.metadata.theme.text === created.data.metadata.theme.text &&
    JSON.stringify(themedData.metadata.layout) === JSON.stringify(created.data.metadata.layout) &&
    JSON.stringify(themedData.metadata.typography) ===
      JSON.stringify(created.data.metadata.typography) &&
    JSON.stringify(themedData.basics.picture) === JSON.stringify(created.data.basics.picture)
  ) {
    return created;
  }

  /*
   * La palette est enregistrée
   * avant l’ouverture du builder.
   *
   * L’utilisateur ne voit donc
   * jamais le modèle avec une
   * couleur intermédiaire erronée.
   */
  const themedResponse = await axios.patch<
    ResumeDto,
    AxiosResponse<ResumeDto>,
    {
      data: ResumeData;
    }
  >(`/resume/${created.id}`, {
    data: themedData,
  });

  return themedResponse.data;
};

export const useCreateResume = () => {
  const {
    error,
    isPending: loading,
    mutateAsync: createResumeFn,
  } = useMutation({
    mutationFn: createResume,

    onSuccess: (data) => {
      queryClient.setQueryData<ResumeDto>(
        [
          "resume",
          {
            id: data.id,
          },
        ],
        data,
      );

      queryClient.setQueryData<ResumeDto[]>(["resumes"], (cache) => {
        if (!cache) {
          return [data];
        }

        return [...cache, data];
      });

      void queryClient.invalidateQueries({
        queryKey: ["balance-credit-creation"],
      });
    },
  });

  return {
    createResume: createResumeFn,
    loading,
    error,
  };
};
